import React, { useState } from 'react';
import { Camera, Music, Users, Play, MapPin, Sparkles } from 'lucide-react';
import '../styles/components/Post.css';

const Post = ({
   captionText = '',
   setCaptionText = () => { },
   selectedOccasion = '',
   setSelectedOccasion = () => { },
   selectedMusic = '',
   setSelectedMusic = () => { },
   selectedCollaborators = [],
   setSelectedCollaborators = () => { },
   occasionOptions = [],
   musicOptions = [],
   collaboratorOptions = []
}) => {
   const [selectedFile, setSelectedFile] = useState(null);
   const [previewUrl, setPreviewUrl] = useState(null);
   const [location, setLocation] = useState('');
   const [postType, setPostType] = useState('photo'); // 'photo' or 'video'

   // Default data if not provided by props
   const defaultOccasionOptions = occasionOptions.length > 0 ? occasionOptions : [
      'Casual', 'Work', 'Date Night', 'Party', 'Brunch', 'Workout', 'Travel', 'Shopping', 'Coffee Date', 'Art Gallery'
   ];

   const defaultMusicOptions = musicOptions.length > 0 ? musicOptions : [
      'Sunday Morning - Maroon 5',
      'Levitating - Dua Lipa',
      'Good 4 U - Olivia Rodrigo',
      'Blinding Lights - The Weeknd',
      'Watermelon Sugar - Harry Styles',
      'positions - Ariana Grande',
      'drivers license - Olivia Rodrigo',
      'As It Was - Harry Styles'
   ];

   const defaultCollaboratorOptions = collaboratorOptions.length > 0 ? collaboratorOptions : [
      { name: 'Sarah Kim', username: '@sarahk', avatar: '👩', following: true },
      { name: 'Alex Chen', username: '@alexc', avatar: '👨', following: true },
      { name: 'Maya Patel', username: '@mayap', avatar: '👩', following: false },
      { name: 'Jordan Lee', username: '@jordanl', avatar: '👨', following: true },
      { name: 'Emma Wilson', username: '@emmastyle', avatar: '👩', following: true },
      { name: 'Marcus Johnson', username: '@marcusfits', avatar: '👨', following: false }
   ];

   const handleFileSelect = (event) => {
      const file = event.target.files[0];
      if (file) {
         setSelectedFile(file);
         const url = URL.createObjectURL(file);
         setPreviewUrl(url);
      }
   };

   const handleCollaboratorToggle = (collab) => {
      if (selectedCollaborators.find(c => c.username === collab.username)) {
         setSelectedCollaborators(selectedCollaborators.filter(c => c.username !== collab.username));
      } else {
         setSelectedCollaborators([...selectedCollaborators, collab]);
      }
   };

   const handlePost = () => {
      const postData = {
         caption: captionText,
         occasion: selectedOccasion,
         music: selectedMusic,
         collaborators: selectedCollaborators,
         location: location,
         type: postType,
         file: selectedFile
      };

      console.log('Creating post:', postData);
      alert('Post shared successfully! 🎉');

      // Reset form
      setCaptionText('');
      setSelectedOccasion('');
      setSelectedMusic('');
      setSelectedCollaborators([]);
      setLocation('');
      setSelectedFile(null);
      setPreviewUrl(null);
   };

   const isFormValid = selectedFile && captionText.trim();

   return (
      <div className="post-container">
         {/* Header */}
         <div className="post-header">
            <h1 className="post-title">Share Your Daily Fit</h1>
            <p className="post-subtitle">Express your style and inspire others</p>
         </div>

         {/* Media Upload Section */}
         <div className="media-upload-section">
            {previewUrl ? (
               <div className="media-preview">
                  {postType === 'photo' ? (
                     <img src={previewUrl} alt="Preview" className="preview-image" />
                  ) : (
                     <video src={previewUrl} className="preview-video" controls />
                  )}
                  <button
                     onClick={() => {
                        setSelectedFile(null);
                        setPreviewUrl(null);
                     }}
                     className="remove-media-btn"
                  >
                     ✕
                  </button>
               </div>
            ) : (
               <div className="upload-placeholder">
                  <Camera size={48} className="upload-icon" />
                  <h3 className="upload-title">Capture Your Look</h3>
                  <p className="upload-description">Take a photo or video of your outfit</p>

                  <div className="upload-buttons">
                     <label className="upload-btn primary">
                        <Camera size={18} />
                        Take Photo
                        <input
                           type="file"
                           accept="image/*"
                           onChange={(e) => {
                              setPostType('photo');
                              handleFileSelect(e);
                           }}
                           className="file-input"
                        />
                     </label>

                     <label className="upload-btn secondary">
                        <Play size={18} />
                        Record Video
                        <input
                           type="file"
                           accept="video/*"
                           onChange={(e) => {
                              setPostType('video');
                              handleFileSelect(e);
                           }}
                           className="file-input"
                        />
                     </label>
                  </div>

                  <button className="gallery-btn">
                     📁 Choose from Gallery
                  </button>
               </div>
            )}
         </div>

         {/* Post Details Form */}
         <div className="post-form">
            {/* Caption Input */}
            <div className="form-group">
               <label className="form-label">
                  <Sparkles size={18} />
                  Write a caption
               </label>
               <textarea
                  value={captionText}
                  onChange={(e) => setCaptionText(e.target.value)}
                  placeholder="What's the vibe today? ✨"
                  className="caption-input"
                  rows={3}
                  maxLength={280}
               />
               <div className="character-count">
                  {captionText.length}/280 characters
               </div>
            </div>

            {/* Occasion Selector */}
            <div className="form-group">
               <label className="form-label">
                  <Sparkles size={18} />
                  What's the occasion?
               </label>
               <div className="occasion-tags">
                  {defaultOccasionOptions.map(occasion => (
                     <button
                        key={occasion}
                        onClick={() => setSelectedOccasion(selectedOccasion === occasion ? '' : occasion)}
                        className={`occasion-tag ${selectedOccasion === occasion ? 'selected' : ''}`}
                     >
                        {occasion}
                     </button>
                  ))}
               </div>
            </div>

            {/* Music Selector */}
            <div className="form-group">
               <label className="form-label">
                  <Music size={18} />
                  Add music to your post
               </label>
               <select
                  value={selectedMusic}
                  onChange={(e) => setSelectedMusic(e.target.value)}
                  className="music-select"
               >
                  <option value="">Choose a song...</option>
                  {defaultMusicOptions.map(song => (
                     <option key={song} value={song}>{song}</option>
                  ))}
               </select>

               <div className="music-suggestions">
                  <p className="suggestions-title">Popular right now:</p>
                  <div className="music-chips">
                     {defaultMusicOptions.slice(0, 3).map((song, index) => (
                        <button
                           key={index}
                           onClick={() => setSelectedMusic(song)}
                           className="music-chip"
                        >
                           <Music size={14} />
                           {song}
                        </button>
                     ))}
                  </div>
               </div>
            </div>

            {/* Location Input */}
            <div className="form-group">
               <label className="form-label">
                  <MapPin size={18} />
                  Add location
               </label>
               <input
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Where was this photo taken?"
                  className="location-input"
               />
            </div>

            {/* Collaborators */}
            <div className="form-group">
               <label className="form-label">
                  <Users size={18} />
                  Tag collaborators
               </label>

               {selectedCollaborators.length > 0 && (
                  <div className="selected-collaborators">
                     {selectedCollaborators.map(collab => (
                        <div key={collab.username} className="selected-collab">
                           <span className="collab-avatar">{collab.avatar}</span>
                           <span className="collab-name">{collab.name}</span>
                           <button
                              onClick={() => handleCollaboratorToggle(collab)}
                              className="remove-collab"
                           >
                              ✕
                           </button>
                        </div>
                     ))}
                  </div>
               )}

               <div className="collaborators-list">
                  {defaultCollaboratorOptions
                     .filter(collab => !selectedCollaborators.find(s => s.username === collab.username))
                     .map(collab => (
                        <button
                           key={collab.username}
                           onClick={() => handleCollaboratorToggle(collab)}
                           className="collaborator-item"
                        >
                           <span className="collab-avatar">{collab.avatar}</span>
                           <div className="collab-info">
                              <span className="collab-name">{collab.name}</span>
                              <span className="collab-username">{collab.username}</span>
                           </div>
                           {collab.following && <span className="following-badge">Following</span>}
                        </button>
                     ))}
               </div>
            </div>

            {/* Post Preview */}
            {(captionText || selectedOccasion || selectedMusic || selectedCollaborators.length > 0 || location) && (
               <div className="post-preview">
                  <h3 className="preview-title">Post Preview</h3>
                  <div className="preview-content">
                     {captionText && <div className="preview-item">📝 "{captionText}"</div>}
                     {selectedOccasion && <div className="preview-item">🎯 {selectedOccasion}</div>}
                     {selectedMusic && <div className="preview-item">🎵 {selectedMusic}</div>}
                     {location && <div className="preview-item">📍 {location}</div>}
                     {selectedCollaborators.length > 0 && (
                        <div className="preview-item">
                           👥 With: {selectedCollaborators.map(c => c.name).join(', ')}
                        </div>
                     )}
                  </div>
               </div>
            )}
         </div>

         {/* Post Actions */}
         <div className="post-actions">
            <button className="draft-btn">
               Save as Draft
            </button>
            <button
               onClick={handlePost}
               disabled={!isFormValid}
               className={`share-btn ${isFormValid ? 'enabled' : 'disabled'}`}
            >
               ✨ Share Your Style ✨
            </button>
         </div>

         {/* Tips Section */}
         <div className="tips-section">
            <h3 className="tips-title">💡 Pro Tips</h3>
            <div className="tips-content">
               <p>• Natural lighting makes your outfit photos pop</p>
               <p>• Show the full outfit for better engagement</p>
               <p>• Tag the occasion to help others discover your style</p>
               <p>• Collaborate with friends to grow your network</p>
            </div>
         </div>
      </div>
   );
};

export default Post;